import React, { useState, useEffect, useCallback } from 'react';
import { complaintAPI, customerAPI, userAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { AlertCircle, Clock, CheckCircle2, X, Plus, Camera, MapPin, ImagePlus, Film } from 'lucide-react';
import useDarkReady from '../../theme/useDarkReady';

// Both broadband and CATV issue types — unlike the CATV-only agent app, a technician
// works both service types (see [[project_link_health]], [[project_olt_ge_port_naming]]).
const COMPLAINT_CATEGORIES = [
  { key: 'no_internet', label: 'No Internet' },
  { key: 'slow_internet', label: 'Slow Internet' },
  { key: 'red_light_router', label: 'Red Light in Router' },
  { key: 'cable_cut', label: 'Cable Cut' },
  { key: 'router_issue', label: 'Router Issue' },
  { key: 'e61_no_signal', label: 'E61 - No Signal' },
  { key: 'e51_channel_error', label: 'E51/999 Channel Error' },
  { key: 'channel_stuck', label: 'ચેનલ ચોટે છે' },
  { key: 'colors_only', label: 'Colors Only' },
  { key: 'relocation_transfer', label: 'Relocation/Transfer' },
  { key: 'device_issue', label: 'Device Issue' },
  { key: 'other', label: 'Other' },
];

// Shared photo/video picker for NewComplaintModal and ResolveModal — same [{file, preview,
// isVideo}] shape and thumbnail-grid layout as the admin ComplaintsPage's picker (up to 5,
// matches complaintMediaUpload's server-side limit on every route that accepts `media`).
function MediaPicker({ images, onAdd, onRemove, label }) {
  const addImages = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - images.length);
    if (!files.length) return;
    onAdd(files.map(file => ({ file, preview: URL.createObjectURL(file), isVideo: file.type.startsWith('video/') })));
    e.target.value = '';
  };

  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {images.map((img, i) => (
          <div key={i} style={{ position: 'relative', width: 64, height: 64 }}>
            {img.isVideo ? (
              <>
                <video src={img.preview} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6, background: 'var(--cb-000000)' }} />
                <Film size={16} color="var(--c-ffffff)" style={{ position: 'absolute', bottom: 4, left: 4, filter: 'drop-shadow(0 0 2px rgba(0,0,0,.8))' }} />
              </>
            ) : (
              <img src={img.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
            )}
            <button type="button" onClick={() => onRemove(i)}
              style={{ position: 'absolute', top: -6, right: -6, background: 'rgba(0,0,0,.6)', border: 'none', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <X size={12} color="var(--c-ffffff)" />
            </button>
          </div>
        ))}
        {images.length < 5 && (
          <label style={{ width: 64, height: 64, border: '1px dashed var(--border)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-muted)' }}>
            <ImagePlus size={20} />
            <input type="file" accept="image/*,video/*" capture="environment" multiple hidden onChange={addImages} />
          </label>
        )}
      </div>
      <p style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 6 }}>Up to 5 photos or videos.</p>
    </div>
  );
}

function NewComplaintModal({ onClose, onDone }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [category, setCategory] = useState(COMPLAINT_CATEGORIES[0].key);
  // Defaults to the category's own label (there's no separate Title field on this form —
  // title is always the category label at submit, see handleSubmit) — staff can still type
  // over it manually.
  const [description, setDescription] = useState(COMPLAINT_CATEGORIES[0].label);
  const [sendWhatsapp, setSendWhatsapp] = useState(true);
  const [addCustomerNote, setAddCustomerNote] = useState(false);
  const [customerNote, setCustomerNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState([]);
  const [assignedTo, setAssignedTo] = useState('');
  const [staff, setStaff] = useState([]);

  useEffect(() => {
    if (customer || search.length < 2) { setResults([]); return; }
    customerAPI.getAll({ search, limit: 10 })
      .then(({ data }) => setResults(data.data || []))
      .catch(() => {});
  }, [search, customer]);

  // Same everyone-with-role-label list the admin Assign dropdown uses — a technician
  // raising a ticket for something outside their own work can hand it straight to
  // whoever should take it, instead of it always landing on themself by default.
  useEffect(() => {
    userAPI.getAll().then(({ data }) => setStaff(data.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!customer) { toast.error('Search and select a customer'); return; }
    if (!description.trim()) { toast.error('Add a description'); return; }
    setSaving(true);
    try {
      const categoryLabel = COMPLAINT_CATEGORIES.find(c => c.key === category)?.label || category;
      const fields = {
        customer: customer._id,
        category,
        title: categoryLabel,
        description: description.trim(),
        serviceType: customer.customerType,
        sendWhatsapp,
        ...(assignedTo ? { assignedTo } : {}),
        ...(sendWhatsapp && addCustomerNote && customerNote.trim() ? { customerNote: customerNote.trim() } : {}),
      };
      let payload = fields;
      if (images.length) {
        payload = new FormData();
        Object.entries(fields).forEach(([k, v]) => payload.append(k, v));
        images.forEach(img => payload.append('media', img.file));
      }
      const { data } = await complaintAPI.create(payload);
      toast.success(`Ticket ${data.data.ticketNumber} created`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create ticket');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !saving && onClose()}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>New Complaint</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Customer *</label>
              {customer ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: '.85rem' }}>
                  <span>
                    <div><strong>{customer.name}</strong> · {customer.customerId} · {customer.mobile}</div>
                    {(customer.address?.area || customer.broadband?.houseNumber || customer.catv?.houseNumber) && (
                      <div style={{ fontSize: '.76rem', color: 'var(--text-muted)' }}>
                        {[customer.address?.area, customer.broadband?.houseNumber || customer.catv?.houseNumber].filter(Boolean).join(' - ')}
                      </div>
                    )}
                  </span>
                  <button type="button" onClick={() => { setCustomer(null); setSearch(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <>
                  <input className="form-control" placeholder="Search customer by name / mobile…"
                    value={search} onChange={e => setSearch(e.target.value)} />
                  {results.length > 0 && (
                    <div style={{ border: '1px solid var(--border)', borderRadius: 6, marginTop: 4, maxHeight: 150, overflowY: 'auto' }}>
                      {results.map(c => (
                        <div key={c._id} onClick={() => { setCustomer(c); setResults([]); }}
                          style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '.85rem', borderBottom: '1px solid var(--border)' }}>
                          <div><strong>{c.name}</strong> · {c.customerId} · {c.mobile}</div>
                          {(c.address?.area || c.broadband?.houseNumber || c.catv?.houseNumber) && (
                            <div style={{ fontSize: '.76rem', color: 'var(--text-muted)' }}>
                              {[c.address?.area, c.broadband?.houseNumber || c.catv?.houseNumber].filter(Boolean).join(' - ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Issue Type</label>
              <select className="form-control" value={category} onChange={e => {
                const newCategory = e.target.value;
                const newLabel = COMPLAINT_CATEGORIES.find(c => c.key === newCategory)?.label || '';
                const oldLabel = COMPLAINT_CATEGORIES.find(c => c.key === category)?.label || '';
                setCategory(newCategory);
                setDescription(d => (!d || d === oldLabel) ? newLabel : d);
              }}>
                {COMPLAINT_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Assign To (optional)</label>
              <select className="form-control" value={assignedTo} onChange={e => setAssignedTo(e.target.value)}>
                <option value="">— Unassigned (defaults to you if raised from Technical) —</option>
                {staff.map(s => <option key={s._id} value={s._id}>{s.name} ({s.role})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Description *</label>
              <textarea className="form-control" rows={3} value={description}
                onChange={e => setDescription(e.target.value)} placeholder="Describe the issue…" />
            </div>
            <MediaPicker images={images} label="Photos / Videos (optional)"
              onAdd={added => setImages(list => [...list, ...added])}
              onRemove={i => setImages(list => list.filter((_, idx) => idx !== i))} />
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={sendWhatsapp} onChange={e => setSendWhatsapp(e.target.checked)} />
                Send WhatsApp update to customer
              </label>
            </div>
            {sendWhatsapp && (
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={addCustomerNote} onChange={e => setAddCustomerNote(e.target.checked)} />
                  Add a note for the customer
                </label>
                {addCustomerNote && (
                  <textarea
                    className="form-control" rows={2} value={customerNote}
                    onChange={e => setCustomerNote(e.target.value)}
                    placeholder="Details to share with the customer…"
                    style={{ resize: 'vertical', marginTop: 6 }}
                  />
                )}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !customer}>
              {saving ? 'Creating…' : 'Create Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const RESOLUTION_TYPES = [
  { value: 'technical_fix', label: 'Technical Fix' },
  { value: 'configuration_change', label: 'Configuration Change' },
  { value: 'hardware_replaced', label: 'Hardware Replaced' },
  { value: 'plan_updated', label: 'Plan Updated' },
  { value: 'no_issue_found', label: 'No Issue Found' },
  { value: 'customer_error', label: 'Customer Error' },
  { value: 'other', label: 'Other' },
];

const STATUS_BADGE = {
  open: { bg: 'var(--cb-fef3c7)', color: 'var(--c-92400e)', label: 'Open' },
  in_progress: { bg: 'var(--cb-dbeafe)', color: 'var(--c-1e40af)', label: 'In Progress' },
  resolved: { bg: 'var(--cb-d1fae5)', color: 'var(--c-065f46)', label: 'Resolved' },
  closed: { bg: 'var(--cb-f3f4f6)', color: 'var(--c-374151)', label: 'Closed' },
  reopened: { bg: 'var(--cb-fee2e2)', color: 'var(--c-991b1b)', label: 'Reopened' },
};

function ResolveModal({ complaint, onClose, onDone }) {
  const [resolutionType, setResolutionType] = useState('technical_fix');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [customerRemark, setCustomerRemark] = useState('');
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!resolutionNotes.trim()) { toast.error('Add a note on how it was resolved'); return; }
    setSaving(true);
    try {
      const fields = {
        status: 'resolved', resolutionType, resolutionNotes: resolutionNotes.trim(),
        customerRemark: customerRemark.trim(),
      };
      let payload = fields;
      if (images.length) {
        payload = new FormData();
        Object.entries(fields).forEach(([k, v]) => payload.append(k, v));
        images.forEach(img => payload.append('media', img.file));
      }
      await complaintAPI.updateStatus(complaint._id, payload);
      toast.success('Marked resolved — admin has been notified');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resolve');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !saving && onClose()}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Resolve {complaint.ticketNumber}</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Resolution Type</label>
              <select className="form-control" value={resolutionType} onChange={e => setResolutionType(e.target.value)}>
                {RESOLUTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">What did you do? * (internal note)</label>
              <textarea className="form-control" rows={4} value={resolutionNotes}
                onChange={e => setResolutionNotes(e.target.value)}
                placeholder="e.g. Replaced faulty patch cord at splitter, ONU re-registered, signal now -19dBm" />
            </div>
            <div className="form-group">
              <label className="form-label">Message to customer (sent via WhatsApp)</label>
              <textarea className="form-control" rows={2} value={customerRemark}
                onChange={e => setCustomerRemark(e.target.value)}
                placeholder="e.g. Your issue has been fixed. Please check and let us know if it persists." />
            </div>
            <MediaPicker images={images} label="Proof of fix (optional)"
              onAdd={added => setImages(list => [...list, ...added])}
              onRemove={i => setImages(list => list.filter((_, idx) => idx !== i))} />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Mark Resolved'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function TechnicalComplaintsPage() {
  useDarkReady();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [resolving, setResolving] = useState(null);
  const [creating, setCreating] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await complaintAPI.getAll(statusFilter ? { status: statusFilter } : {});
      setComplaints(data.data);
    } catch {
      toast.error('Failed to load complaints');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const startWork = async (complaint) => {
    try {
      await complaintAPI.updateStatus(complaint._id, { status: 'in_progress' });
      toast.success('Marked in progress');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    }
  };

  const handleAddPhoto = async (complaintId, file) => {
    if (!file) return;
    setUploadingId(complaintId);
    try {
      const formData = new FormData();
      formData.append('media', file);
      await complaintAPI.addAttachment(complaintId, formData);
      toast.success('Added to ticket');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add attachment');
    } finally {
      setUploadingId(null);
    }
  };

  const openCounts = { open: 0, in_progress: 0, resolved: 0 };
  complaints.forEach(c => { if (openCounts[c.status] !== undefined) openCounts[c.status]++; });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>My Complaints</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '.85rem', margin: '4px 0 0' }}>
            Tickets assigned to you. Resolve here once fixed — admin is notified automatically.
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setCreating(true)} style={{ flexShrink: 0 }}>
          <Plus size={14} /> New Complaint
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[['', 'All'], ['open', 'Open'], ['in_progress', 'In Progress'], ['resolved', 'Resolved']].map(([v, label]) => (
          <button key={v} className={`btn btn-sm ${statusFilter === v ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setStatusFilter(v)}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : complaints.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
          <CheckCircle2 size={28} style={{ marginBottom: 8 }} />
          <div>No complaints assigned to you{statusFilter ? ` (${STATUS_BADGE[statusFilter]?.label})` : ''}.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {complaints.map(c => {
            const badge = STATUS_BADGE[c.status] || {};
            return (
              <div key={c._id} style={{ border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, padding: 14, background: 'var(--cb-ffffff)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontFamily: 'monospace', fontSize: '.78rem', color: 'var(--c-6b7280)' }}>{c.ticketNumber}</span>
                      <span style={{ fontSize: '.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: badge.bg, color: badge.color }}>
                        {badge.label}
                      </span>
                      {c.priority === 'critical' && (
                        <span style={{ fontSize: '.68rem', fontWeight: 700, color: 'var(--c-d03b3b)', display: 'flex', alignItems: 'center', gap: 3 }}>
                          <AlertCircle size={11} /> Critical
                        </span>
                      )}
                    </div>
                    <div style={{ fontWeight: 600, marginTop: 4 }}>{c.title}</div>
                    <div style={{ fontSize: '.82rem', color: 'var(--c-6b7280)', marginTop: 2 }}>
                      {c.customer?.name} ({c.customer?.customerId}) — {c.description}
                    </div>
                    {(c.customer?.address?.area || c.customer?.broadband?.houseNumber || c.customer?.catv?.houseNumber) && (
                      <div style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <MapPin size={11} />
                        {[c.customer?.address?.area, c.customer?.broadband?.houseNumber || c.customer?.catv?.houseNumber].filter(Boolean).join(' - ')}
                      </div>
                    )}
                    {c.status === 'resolved' && c.resolutionNotes && (
                      <div style={{ fontSize: '.78rem', color: 'var(--c-065f46)', marginTop: 6, background: 'var(--cb-f0fdf4)', padding: '6px 8px', borderRadius: 6 }}>
                        Resolved: {c.resolutionNotes}
                      </div>
                    )}
                    {!c.attachments?.length && (
                      <label
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 8,
                          fontSize: '.78rem', fontWeight: 600, color: 'var(--primary)',
                          border: '1px solid var(--primary)', borderRadius: 6, padding: '4px 10px',
                          cursor: uploadingId === c._id ? 'default' : 'pointer',
                          opacity: uploadingId === c._id ? .6 : 1,
                        }}
                      >
                        <Camera size={12} /> {uploadingId === c._id ? 'Uploading…' : 'Add Photo/Video'}
                        <input
                          type="file" accept="image/*,video/*" capture="environment" style={{ display: 'none' }}
                          disabled={uploadingId === c._id}
                          onChange={e => { handleAddPhoto(c._id, e.target.files[0]); e.target.value = ''; }}
                        />
                      </label>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    {c.status === 'open' && (
                      <button className="btn btn-secondary btn-sm" onClick={() => startWork(c)}>
                        <Clock size={12} /> Start
                      </button>
                    )}
                    {(c.status === 'open' || c.status === 'in_progress') && (
                      <button className="btn btn-primary btn-sm" onClick={() => setResolving(c)}>
                        <CheckCircle2 size={12} /> Resolve
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {resolving && (
        <ResolveModal complaint={resolving} onClose={() => setResolving(null)}
          onDone={() => { setResolving(null); load(); }} />
      )}

      {creating && (
        <NewComplaintModal onClose={() => setCreating(false)}
          onDone={() => { setCreating(false); load(); }} />
      )}
    </div>
  );
}
